import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useToast } from '@chakra-ui/react';
import { useGetSettings } from '../../hooks/useGetSettings';
import { useUpdateSettings, type UpdatedSettings } from '../../hooks/useUpdateSettings';

/** The subdocuments of the settings document that sections edit. */
export type SettingsDomain = Exclude<keyof UpdatedSettings, 'activityContext'>;

interface SettingsDraftContextValue {
    /** Pending edits, keyed by domain. Absent means untouched. */
    drafts: Partial<UpdatedSettings>;
    /** Replaces a domain wholesale — always pass the complete subdocument. */
    setDomain: <K extends SettingsDomain>(domain: K, value: UpdatedSettings[K]) => void;
    /** Drops a domain from the draft (no-op if absent). */
    clearDomain: (domain: SettingsDomain) => void;
    /** Current value for a domain, falling back to the server value. */
    getDomain: <K extends SettingsDomain>(domain: K, serverValue: UpdatedSettings[K]) => UpdatedSettings[K];
    /**
     * Confirmed-or-server baseline for a domain (ignores drafts). Use when
     * comparing an edit back to the last saved value before refetch lands.
     */
    getBaseline: <K extends SettingsDomain>(domain: K, serverValue: UpdatedSettings[K]) => UpdatedSettings[K];
    isDirty: boolean;
    isSaving: boolean;
    save: () => Promise<void>;
    discard: () => void;
    /**
     * Registers a check that runs before a save. Returning a message aborts
     * the save and surfaces that message instead. Returns an unregister fn.
     */
    registerValidator: (key: string, validate: () => string | null) => () => void;
}

const SettingsDraftContext = createContext<SettingsDraftContextValue | undefined>(undefined);

export interface SettingsDraftProviderProps {
    /** Called after a successful save so the section can refetch. */
    onSaved?: () => void;
    children: ReactNode;
}

/**
 * Buffers settings edits so a section can offer save and discard rather than
 * writing on every toggle.
 *
 * `POST /settings/update` replaces a whole subdocument, so drafts are stored
 * per domain and each must hold the complete object. Several sections share a
 * domain (the six invoicing sections all write `invoicingSettings`), which is
 * why the buffer lives above them: one save sends every dirty domain in a
 * single request instead of racing partial writes.
 */
export const SettingsDraftProvider = ({ onSaved, children }: SettingsDraftProviderProps) => {
    const [drafts, setDrafts] = useState<Partial<UpdatedSettings>>({});
    // Last successful save, kept until our refetch lands so getDomain never
    // falls back to the pre-save query for a frame.
    const [confirmed, setConfirmed] = useState<Partial<UpdatedSettings>>({});
    const confirmedUntil = useRef<number | null>(null);
    const { refetch, dataUpdatedAt } = useGetSettings();
    const updateSettings = useUpdateSettings();
    const toast = useToast();
    // A ref, not state: registering a check must not re-render the section that
    // registered it, and validators only ever run inside save().
    const validators = useRef(new Map<string, () => string | null>());

    useEffect(() => {
        if (confirmedUntil.current == null) return;
        if (dataUpdatedAt <= confirmedUntil.current) return;
        confirmedUntil.current = null;
        setConfirmed({});
    }, [dataUpdatedAt]);

    const registerValidator = useCallback((key: string, validate: () => string | null) => {
        validators.current.set(key, validate);
        return () => {
            validators.current.delete(key);
        };
    }, []);

    const setDomain = useCallback(
        <K extends SettingsDomain>(domain: K, value: UpdatedSettings[K]) => {
            setDrafts((prev) => ({ ...prev, [domain]: value }));
        },
        [],
    );

    const clearDomain = useCallback((domain: SettingsDomain) => {
        setDrafts((prev) => {
            if (!(domain in prev)) return prev;
            const next = { ...prev };
            delete next[domain];
            return next;
        });
    }, []);

    const getDomain = useCallback(
        <K extends SettingsDomain>(domain: K, serverValue: UpdatedSettings[K]): UpdatedSettings[K] =>
            (drafts[domain] as UpdatedSettings[K] | undefined)
            ?? (confirmed[domain] as UpdatedSettings[K] | undefined)
            ?? serverValue,
        [drafts, confirmed],
    );

    const getBaseline = useCallback(
        <K extends SettingsDomain>(domain: K, serverValue: UpdatedSettings[K]): UpdatedSettings[K] =>
            (confirmed[domain] as UpdatedSettings[K] | undefined) ?? serverValue,
        [confirmed],
    );

    const isDirty = Object.keys(drafts).length > 0;

    const save = useCallback(async () => {
        if (!isDirty) return;
        for (const validate of Array.from(validators.current.values())) {
            const message = validate();
            if (message) {
                toast({
                    title: 'Cannot save changes',
                    description: message,
                    status: 'warning',
                    duration: 5000,
                    isClosable: true,
                });
                return;
            }
        }
        try {
            const updatedAtBefore = dataUpdatedAt;
            await updateSettings.mutateAsync(drafts);
            confirmedUntil.current = updatedAtBefore;
            setConfirmed(drafts);
            setDrafts({});
            await refetch();
            onSaved?.();
            toast({
                title: 'Changes saved',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch {
            toast({
                title: 'Failed to save changes',
                description: 'An unexpected error occurred.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    }, [dataUpdatedAt, drafts, isDirty, onSaved, refetch, toast, updateSettings]);

    const discard = useCallback(() => {
        confirmedUntil.current = null;
        setConfirmed({});
        setDrafts({});
    }, []);

    const value = useMemo<SettingsDraftContextValue>(
        () => ({
            drafts,
            setDomain,
            clearDomain,
            getDomain,
            getBaseline,
            isDirty,
            isSaving: updateSettings.isPending,
            save,
            discard,
            registerValidator,
        }),
        [
            drafts,
            setDomain,
            clearDomain,
            getDomain,
            getBaseline,
            isDirty,
            updateSettings.isPending,
            save,
            discard,
            registerValidator,
        ],
    );

    return <SettingsDraftContext.Provider value={value}>{children}</SettingsDraftContext.Provider>;
};

export const useSettingsDraft = (): SettingsDraftContextValue => {
    const ctx = useContext(SettingsDraftContext);
    if (!ctx) {
        throw new Error('useSettingsDraft must be used inside a SettingsDraftProvider');
    }
    return ctx;
};

/**
 * Blocks the section's save while `validate` returns a message. The callback is
 * held in a ref so it always sees the latest render's values without the caller
 * having to memoize it.
 */
export const useSettingsDraftValidator = (key: string, validate: () => string | null) => {
    const { registerValidator } = useSettingsDraft();
    const latest = useRef(validate);
    latest.current = validate;

    useEffect(() => registerValidator(key, () => latest.current()), [key, registerValidator]);
};

export default SettingsDraftContext;
